/**
 * Job Message Utilities
 *
 * Functions for generating user-facing messages (toasts, notifications, etc.)
 */

/**
 * Result type for bulk actions
 */
export type BulkActionResult = 'success' | 'partial' | 'error';

/**
 * Bulk action message structure
 */
export interface BulkActionMessage {
  type: BulkActionResult;
  message: string;
}

/**
 * Builds a toast message for bulk job actions
 *
 * Handles three scenarios:
 * - All succeeded: success message
 * - Some succeeded, some failed: warning message
 * - All failed: error message
 *
 * @param action - Past tense action verb (e.g., 'reran', 'canceled')
 * @param succeeded - Number of successful operations
 * @param failed - Number of failed operations
 * @returns Message object with type and text
 *
 * @example
 * const msg = buildBulkActionMessage('reran', 5, 2);
 * // Returns: { type: 'partial', message: 'Reran 5 jobs, but failed for 2 jobs' }
 */
export const buildBulkActionMessage = (
  action: string,
  succeeded: number,
  failed: number
): BulkActionMessage => {
  const jobWord = (count: number) => (count === 1 ? 'job' : 'jobs');
  const capitalizedAction = action.charAt(0).toUpperCase() + action.slice(1);

  // Convert past tense to infinitive for error messages
  const infinitive =
    action === 'reran' ? 'rerun' : action === 'canceled' ? 'cancel' : action;

  if (succeeded > 0 && failed === 0) {
    return {
      type: 'success',
      message: `${capitalizedAction} ${succeeded} ${jobWord(succeeded)} successfully`,
    };
  }

  if (succeeded > 0 && failed > 0) {
    return {
      type: 'partial',
      message: `${capitalizedAction} ${succeeded} ${jobWord(succeeded)}, but failed for ${failed} ${jobWord(failed)}`,
    };
  }

  return {
    type: 'error',
    message: `Failed to ${infinitive} ${failed} ${jobWord(failed)}`,
  };
};

/**
 * Aggregates Promise.allSettled results into success/failure counts
 *
 * @param results - Results from Promise.allSettled
 * @returns Object with succeeded and failed counts
 */
export const aggregatePromiseResults = <T>(
  results: PromiseSettledResult<T>[]
): { succeeded: number; failed: number } => {
  const succeeded = results.filter(r => r.status === 'fulfilled').length;
  const failed = results.filter(r => r.status === 'rejected').length;
  return { succeeded, failed };
};
